import { AppContext } from 'contexts/app-context';
import {
  FeatureFlagContext,
  FeatureFlags,
} from 'contexts/feature-flag-context';
import { AppContext as AppContextV2 } from 'contexts/v2/app-context';
import { useLanguage } from 'i18n/LanguageProvider';
import React, { useContext, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { RouteParams } from 'types';

import { Footer } from 'components/footer';
import { Home } from 'components/home';
import { HomeLoading } from 'components/home-loading';
import { Nav } from 'components/nav';
import { PrivacyPolicy } from 'components/privacy-policy';
import { Home as HomeV2 } from 'components/v2/home';
import { useBrandConfig } from 'hooks/useBrandConfig';
import { useUrlFilters } from 'hooks/useUrlFilters';
import { useBrandConfigV2 } from 'hooks/v2/useBrandConfig';
import { useUrlFiltersV2 } from 'hooks/v2/useUrlFilters';

const isPrivacyPath = (pathname: string): boolean =>
  /\/privacy\/?$/.test(pathname);

const V1Main = () => {
  const { accountSlug, brandId } = useParams<RouteParams>();
  const {
    activeFilters,
    setBrandDetails,
    setActiveFilters,
    setPage,
    setPlaceId,
    setPosition,
    setIsJobsFromCurrentLocation,
  } = useContext(AppContext);
  const { setFlags } = useContext(FeatureFlagContext);
  const { search, pathname } = useLocation();
  const { currentLanguage, setDefaultLocale, setLanguageConfig } =
    useLanguage();

  const { data: brandConfig, isFetching: isFetchingBrandConfig } =
    useBrandConfig(accountSlug, brandId, currentLanguage);

  // Set language configuration when brandConfig loads
  useEffect(() => {
    if (brandConfig) {
      setLanguageConfig({
        configuredLanguages: brandConfig.configured_languages ?? [],
        isLanguageSelectorEnabled:
          brandConfig.settings?.jobs_directory_translations ?? false,
      });
    }
  }, [brandConfig, setLanguageConfig]);

  useEffect(() => {
    const palette = brandConfig?.pallete_colors?.palette;

    if (palette) {
      const rootElem = document.querySelector(':root') as HTMLHeadElement;
      rootElem.style.setProperty('--primary-50', palette['50'] as string);
      rootElem.style.setProperty('--primary-100', palette['100'] as string);
      rootElem.style.setProperty('--primary-200', palette['200'] as string);
      rootElem.style.setProperty('--primary-300', palette['300'] as string);
      rootElem.style.setProperty('--primary-400', palette['400'] as string);
      rootElem.style.setProperty('--primary-500', palette['500'] as string);
      rootElem.style.setProperty('--primary-600', palette['600'] as string);
      rootElem.style.setProperty('--primary-700', palette['700'] as string);
      rootElem.style.setProperty('--primary-800', palette['800'] as string);
      rootElem.style.setProperty('--primary-900', palette['900'] as string);
      rootElem.style.setProperty('--primary-950', palette['950'] as string);
    }
  }, [brandConfig?.pallete_colors.palette]);

  useEffect(() => {
    setBrandDetails(accountSlug, brandId, brandConfig?.name ?? '');
  }, [brandConfig, accountSlug, brandId, setBrandDetails]);

  useEffect(() => {
    if (brandConfig?.feature_flags) {
      const flags: FeatureFlags = {
        careerSiteSettings: brandConfig.feature_flags.career_site_settings,
        careerSiteInterdependentFilters:
          brandConfig.feature_flags.career_site_interdependent_filters,
        careerSitePrivacyPolicy:
          brandConfig.feature_flags.career_site_privacy_policy,
      };
      setFlags(flags);
    }
  }, [brandConfig?.feature_flags, setFlags]);

  useEffect(() => {
    if (brandConfig?.settings?.default_locale) {
      setDefaultLocale(brandConfig.settings.default_locale);
    }
  }, [brandConfig?.settings?.default_locale, setDefaultLocale]);

  // Handle URL parameter parsing and filter setting
  useUrlFilters({
    brandConfig,
    search,
    activeFilters,
    setActiveFilters,
    currentCity: 'Current Location',
    setPage,
    setPlaceId,
    setPosition,
    setIsJobsFromCurrentLocation,
  });

  if (isFetchingBrandConfig) {
    return <HomeLoading />;
  }

  if (!brandConfig) {
    return <div className="flex justify-center">No Brand Found</div>;
  }

  const showPrivacy =
    isPrivacyPath(pathname) &&
    brandConfig.feature_flags.career_site_privacy_policy;

  return (
    <>
      <Nav
        logoUrl={brandConfig.logo_url}
        brandName={brandConfig.name}
        showWidgetByDefault={brandConfig?.settings?.show_faq_chatbot_by_default}
        showChatAgentWidget={brandConfig?.settings?.show_chat_agent_widget}
        chatAgentConfig={brandConfig?.settings?.chat_agent_config}
        ssoEnabled={brandConfig.settings?.sso_enabled}
        ssoDetails={brandConfig.sso}
        configuredLanguages={brandConfig.configured_languages}
      />
      {showPrivacy ? (
        <PrivacyPolicy
          accountName={brandConfig.name}
          supportContact={brandConfig.support_contact}
        />
      ) : (
        <Home
          bannerUrl={brandConfig.banner_url}
          brandSetting={brandConfig.settings}
          radiusOptions={brandConfig.radius_options}
          categoryOptions={brandConfig.category_options}
          departmentOptions={brandConfig.departments}
          compensationTypeOptions={brandConfig.compensation_type_options}
        />
      )}
      {brandConfig.feature_flags.career_site_privacy_policy && (
        <Footer accountName={brandConfig.name} />
      )}
    </>
  );
};

const V2Main = () => {
  const { accountSlug, brandId } = useParams<RouteParams>();
  const {
    activeFiltersV2,
    setActiveFiltersV2,
    setPage,
    setPlaceId,
    setIsJobsFromCurrentLocation,
    setBrandDetails,
    setJobSearchQuery,
    setLocation,
    setDisplayLocation,
    setRadius,
    setSortBy,
    location,
    radius,
    sortBy,
    jobSearchQuery,
  } = useContext(AppContextV2);
  const { setFlags } = useContext(FeatureFlagContext);
  const { search, pathname } = useLocation();
  const { currentLanguage, setDefaultLocale, setLanguageConfig } =
    useLanguage();

  const { data: brandConfig, isFetching: isFetchingBrandConfig } =
    useBrandConfigV2(accountSlug, brandId, currentLanguage);

  // Set language configuration when brandConfig loads
  useEffect(() => {
    if (brandConfig) {
      setLanguageConfig({
        configuredLanguages: brandConfig.configured_languages ?? [],
        isLanguageSelectorEnabled:
          brandConfig.settings?.jobs_directory_translations ?? false,
      });
    }
  }, [brandConfig, setLanguageConfig]);

  useEffect(() => {
    const palette = brandConfig?.pallete_colors?.palette;

    if (palette) {
      const rootElem = document.querySelector(':root') as HTMLHeadElement;
      rootElem.style.setProperty('--primary-50', palette['50'] as string);
      rootElem.style.setProperty('--primary-100', palette['100'] as string);
      rootElem.style.setProperty('--primary-200', palette['200'] as string);
      rootElem.style.setProperty('--primary-300', palette['300'] as string);
      rootElem.style.setProperty('--primary-400', palette['400'] as string);
      rootElem.style.setProperty('--primary-500', palette['500'] as string);
      rootElem.style.setProperty('--primary-600', palette['600'] as string);
      rootElem.style.setProperty('--primary-700', palette['700'] as string);
      rootElem.style.setProperty('--primary-800', palette['800'] as string);
      rootElem.style.setProperty('--primary-900', palette['900'] as string);
      rootElem.style.setProperty('--primary-950', palette['950'] as string);
    }
  }, [brandConfig?.pallete_colors.palette]);

  useEffect(() => {
    setBrandDetails(accountSlug, brandId, brandConfig?.name ?? '');
  }, [brandConfig, accountSlug, brandId, setBrandDetails]);

  useEffect(() => {
    if (brandConfig?.feature_flags) {
      const flags: FeatureFlags = {
        careerSiteSettings: brandConfig.feature_flags.career_site_settings,
        careerSiteInterdependentFilters:
          brandConfig.feature_flags.career_site_interdependent_filters,
        careerSitePrivacyPolicy:
          brandConfig.feature_flags.career_site_privacy_policy,
      };
      setFlags(flags);
    }
  }, [brandConfig?.feature_flags, setFlags]);

  useEffect(() => {
    if (brandConfig?.settings?.default_locale) {
      setDefaultLocale(brandConfig.settings.default_locale);
    }
  }, [brandConfig?.settings?.default_locale, setDefaultLocale]);

  useEffect(() => {
    if (brandConfig?.settings) {
      setLocation({ id: null, value: 'current_location' });
      setIsJobsFromCurrentLocation(true);
    }
  }, [brandConfig?.settings, setLocation, setIsJobsFromCurrentLocation]);

  useUrlFiltersV2({
    brandConfig,
    search,
    activeFiltersV2,
    setActiveFiltersV2,
    setPage,
    setPlaceId,
    setIsJobsFromCurrentLocation,
    setJobSearchQuery,
    setLocation,
    setDisplayLocation,
    setRadius,
    setSortBy,
    location,
    radius,
    sortBy,
    jobSearchQuery,
  });

  if (isFetchingBrandConfig) {
    return <HomeLoading />;
  }

  if (!brandConfig) {
    return <div className="flex justify-center">No Brand Found</div>;
  }

  const showPrivacy =
    isPrivacyPath(pathname) &&
    brandConfig.feature_flags.career_site_privacy_policy;

  return (
    <>
      <Nav
        logoUrl={brandConfig.logo_url}
        brandName={brandConfig.name}
        showWidgetByDefault={brandConfig?.settings?.show_faq_chatbot_by_default}
        showChatAgentWidget={brandConfig?.settings?.show_chat_agent_widget}
        chatAgentConfig={brandConfig?.settings?.chat_agent_config}
        ssoEnabled={brandConfig.settings?.sso_enabled}
        ssoDetails={brandConfig.sso}
        configuredLanguages={brandConfig.configured_languages}
      />
      {showPrivacy ? (
        <PrivacyPolicy
          accountName={brandConfig.name}
          supportContact={brandConfig.support_contact}
        />
      ) : (
        <HomeV2
          bannerUrl={brandConfig.banner_url}
          brandSetting={brandConfig.settings}
          radiusOptions={brandConfig.radius_options}
          filterOptions={{
            category: brandConfig.filter_options?.category,
            position: brandConfig.filter_options?.position,
            locationName: brandConfig.filter_options?.locationName,
            jobTypes: brandConfig.filter_options?.job_types,
            jobHours: brandConfig.filter_options?.job_hours,
            countries: brandConfig.filter_options?.countries,
            states: brandConfig.filter_options?.states,
            cities: brandConfig.filter_options?.cities,
          }}
        />
      )}
      {brandConfig.feature_flags.career_site_privacy_policy && (
        <Footer accountName={brandConfig.name} />
      )}
    </>
  );
};

export const Main = () => {
  const { featureFlags } = useContext(FeatureFlagContext);
  return featureFlags.careerSiteInterdependentFilters ? <V2Main /> : <V1Main />;
};
